import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconArrowLeft,
  IconCheck,
  IconDownload,
  IconEye,
  IconFileText,
  IconPencil,
  IconSend,
  IconTrash,
  IconX,
} from "@tabler/icons-react"

interface Attachment {
  id: number
  filename: string
  byte_size: number
  created_at: string
  view_url: string
  download_url: string
}

interface TimeOffRequest {
  id: number
  request_type: string
  status: string
  start_date: string
  end_date: string
  days_requested: number
  hours_requested: number | null
  request_type_unit: string
  notes: string | null
  user_name: string
  created_at: string
  updated_at: string
  can_edit: boolean
  can_cancel: boolean
  can_delete: boolean
  attachments: Attachment[]
}

interface Props {
  [key: string]: unknown
  time_off_request: TimeOffRequest
}

const STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
  approved: { variant: "default", label: "Approved" },
  pending: { variant: "secondary", label: "Pending" },
  denied: { variant: "destructive", label: "Denied" },
  cancelled: { variant: "outline", label: "Cancelled" },
}

function formatDateLong(dateStr: string) {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

function durationDisplay(req: TimeOffRequest) {
  if (req.request_type_unit === "hours" && req.hours_requested) {
    return `${req.hours_requested}h`
  }
  return `${req.days_requested}d`
}

export default function TimeOffRequestShow() {
  const { time_off_request: request } = usePage<Props>().props
  const badge = STATUS_BADGE[request.status] || { variant: "outline" as const, label: request.status }

  function handleCancel() {
    if (
      confirm(
        `Are you sure you want to cancel this request?${request.status === "approved" ? " The time will be restored to your balance." : ""}`,
      )
    ) {
      router.patch(`/time_off_requests/${request.id}/cancel`)
    }
  }

  function handleDelete() {
    if (confirm("Are you sure you want to delete this request? This action cannot be undone.")) {
      router.delete(`/time_off_requests/${request.id}`)
    }
  }

  return (
    <>
      <Head title={`Request #${request.id} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/time_off_requests">My Requests</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Request #{request.id}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/time_off_requests">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-lg font-semibold tracking-tight">
                    {request.request_type.charAt(0).toUpperCase() + request.request_type.slice(1)} Leave
                  </h1>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  {formatDate(request.start_date)} &mdash; {formatDate(request.end_date)} &middot;{" "}
                  {durationDisplay(request)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {request.can_delete && (
                <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={handleDelete}>
                  <IconTrash className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
              )}
              {request.can_cancel && (
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  <IconX className="mr-1.5 h-4 w-4" />
                  Cancel
                </Button>
              )}
              {request.can_edit && (
                <Button size="sm" asChild>
                  <a href={`/time_off_requests/${request.id}/edit`}>
                    <IconPencil className="mr-1.5 h-4 w-4" />
                    Edit
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Main */}
          <div className="xl:col-span-2 space-y-6">
            {/* Details */}
            <div className="rounded-xl border bg-card">
              <div className="grid grid-cols-2 gap-px sm:grid-cols-4">
                <DetailCell label="Type" value={request.request_type} capitalize />
                <DetailCell
                  label="Duration"
                  value={
                    request.request_type_unit === "hours" && request.hours_requested
                      ? `${request.hours_requested} hours`
                      : `${request.days_requested} days`
                  }
                />
                <DetailCell label="From" value={formatDateLong(request.start_date)} />
                <DetailCell label="To" value={formatDateLong(request.end_date)} />
              </div>

              {request.notes && (
                <>
                  <Separator />
                  <div className="p-5">
                    <p className="text-muted-foreground mb-1.5 text-xs font-medium uppercase tracking-wider">Notes</p>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{request.notes}</p>
                  </div>
                </>
              )}
            </div>

            {/* Attachments */}
            {request.attachments.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Attachments</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {request.attachments.map((att) => (
                    <div
                      key={att.id}
                      className="group flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                    >
                      <div className="bg-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                        <IconFileText className="text-muted-foreground h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{att.filename}</p>
                        <p className="text-muted-foreground text-xs">{formatBytes(att.byte_size)}</p>
                      </div>
                      <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                          <a href={att.view_url} target="_blank" rel="noopener noreferrer">
                            <IconEye className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                          <a href={att.download_url}>
                            <IconDownload className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Timeline */}
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-4 text-sm font-medium">Activity</h3>
              <div className="relative space-y-4">
                <div className="border-border absolute left-[11px] top-2 bottom-2 w-px border-l border-dashed" />
                <TimelineItem
                  icon={<IconSend className="h-3 w-3" />}
                  title="Submitted"
                  subtitle={`by ${request.user_name}`}
                  date={formatDateTime(request.created_at)}
                />
                {request.status !== "pending" && (
                  <TimelineItem
                    icon={
                      request.status === "approved" ? (
                        <IconCheck className="h-3 w-3 text-green-600 dark:text-green-400" />
                      ) : (
                        <IconX className="h-3 w-3" />
                      )
                    }
                    title={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    date={formatDateTime(request.updated_at)}
                  />
                )}
              </div>
            </div>

            {/* Meta */}
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">Details</h3>
              <dl className="space-y-2.5 text-sm">
                <MetaRow label="Requested by" value={request.user_name} />
                <MetaRow label="Request ID" value={`#${request.id}`} />
                <MetaRow label="Submitted" value={formatDateTime(request.created_at)} />
                {request.status !== "pending" && <MetaRow label="Updated" value={formatDateTime(request.updated_at)} />}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function DetailCell({ label, value, capitalize: cap }: { label: string; value: string; capitalize?: boolean }) {
  return (
    <div className="p-5">
      <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-medium ${cap ? "capitalize" : ""}`}>{value}</p>
    </div>
  )
}

function TimelineItem({
  icon,
  title,
  subtitle,
  date,
}: {
  icon: React.ReactNode
  title: string
  subtitle?: string
  date: string
}) {
  return (
    <div className="relative flex items-start gap-3">
      <div className="bg-muted relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
        {icon}
      </div>
      <div className="pt-0.5">
        <p className="text-sm font-medium">
          {title}
          {subtitle && <span className="text-muted-foreground font-normal"> {subtitle}</span>}
        </p>
        <p className="text-muted-foreground text-xs">{date}</p>
      </div>
    </div>
  )
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}

TimeOffRequestShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
